import { CboRole, SiteInfo } from '@cbo/shared-library';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Chip from '@mui/material/Chip';
import { Controller, ControllerRenderProps, FieldValues, useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import WestIcon from '@mui/icons-material/West';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Alert from '@mui/material/Alert';
import { DebouncedFunc, isNil } from 'lodash';
import FilterContainer from '../FilterFrame/FilterFrame';
import SearchBar from '../SearchBar/SearchBar';
import TabBar, { getUniqueTabs } from '../TabBar/TabBar';
import { BaseGlobalFilterProps, ButtonSize, DateComparisonRanges, SingleSelectGroup } from '../types';
import SingleSelectButton from './SingleSelectButton';
import SingleSelectTextField from './SingleSelectTextField';
import * as styles from './styles';
import { useSites } from '../../../contexts/siteContext';
import SiteFilterButton from '../SiteFilter/SiteFilterButton';
import { getSiteInfo } from '../../../utils/reportingUtils/filterUtils';
import { Jurisdiction, JurisdictionResponse } from '../../../laborRules/models/JurisdictionModels';
import { useUsers } from '../../../contexts/userContext';
import isPermitted from '../../../lib/permissions';

interface ExpandedMenuItemProps {
  onClick: () => void;
  text: string;
  backgroundColor: string;
}

function ExpandedMenuItem({
  onClick,
  text,
  backgroundColor,
  children,
}: React.PropsWithChildren<ExpandedMenuItemProps>) {
  return (
    <MenuItem
      id={`${text}-id`}
      data-testid={`${text}-id`}
      aria-label={text}
      role={undefined}
      key={text}
      sx={styles.getExpandedMenuItemStyles(backgroundColor)}
      onClick={onClick}
    >
      {children}
    </MenuItem>
  );
}

/**
 * @param options Array of options with type SingleSelectGroup that appear on the filter
 * @param useTabs Whether to use tabs within the component
 * @param predefined Whether the filter should have a predefined value
 * @param useAsDateComparison Internally used to handle date range comparison. Do not use this prop.
 * @param currentSite The default value for the site filter
 * @param defaultDateComparisonPeriod Internally used to handle date range comparison. Do not use this prop here, only use it with the DateComparison component.
 * @param jurisdictionData The data for jurisdictions, to be used in labor rules page for nested jurisdictions
 * @param errorHandling An object that contains a boolean to show an error message and a function to pass in a refetch function that is called when the refresh button is clicked
 * @param preDefinedFilterSelection If it's true Enables pre-defined filter selection UI as pre-selected (first option or default).
 * @param debouncedSearchFn Debounced function for querying BE with search value
 * @param customStartIcon Custom icon to display at the start of the button when predefined is true
 * @param isTextField Optional, Whether to render as a text field
 * @param textFieldLabel Optional, the label for the text field
 * @param isRequired Optional, whether the field is required
 */
export interface SingleSelectProps extends BaseGlobalFilterProps {
  options: SingleSelectGroup[];
  preDefinedFilterSelection?: boolean;
  useTabs?: boolean;
  predefined?: boolean;
  useAsDateComparison?: boolean;
  currentSite?: SingleSelectGroup;
  defaultDateComparisonPeriod?: number;
  jurisdictionData?: JurisdictionResponse;
  labelText?: string;
  useHeader?: boolean;
  useReset?: boolean;
  genericTabs?: string[];
  isDrawerSelect?: boolean;
  errorHandling?: {
    showError: boolean;
    refetch: () => Promise<unknown>;
  };
  isDropDownSelect?: boolean;
  hasError?: boolean;
  disabled?: boolean;
  required?: boolean;
  debouncedSearchFn?: DebouncedFunc<(searchValue: string | undefined) => void>;
  customStartIcon?: React.ReactNode;
  isTextField?: boolean;
  textFieldLabel?: string;
  isRequired?: boolean;
}

type ExtendedJurisdiction = Jurisdiction & {
  pathSegments: string[];
};

function SingleSelect(props: SingleSelectProps) {
  const {
    useTabs,
    options,
    buttonText,
    titleText = '',
    onlyFilterContainer,
    controllerName,
    buttonSize = ButtonSize.SMALL,
    predefined,
    useAsDateComparison,
    currentSite,
    defaultDateComparisonPeriod = 0,
    jurisdictionData,
    errorHandling,
    labelText,
    useHeader,
    useReset,
    genericTabs,
    isDrawerSelect,
    preDefinedFilterSelection,
    isDropDownSelect,
    hasError,
    disabled,
    required,
    debouncedSearchFn,
    customStartIcon,
    isTextField,
    textFieldLabel,
    isRequired,
  } = props;

  const { t } = useTranslation();
  const user = useUsers();
  const isUserLREWriter = isPermitted(user, [CboRole.LRE_RULE_WRITER]);

  const jurisdictionDataCleaned = useMemo(
    () =>
      jurisdictionData && {
        ...jurisdictionData,
        jurisdictions: jurisdictionData.jurisdictions
          .map((juris) => ({
            ...juris,
            pathSegments: juris.path.split('.').map((segment) => segment.replaceAll('_', ' ')),
          }))
          // Non-admin users should not see jurisdictions without sites
          .filter((juris) => (isUserLREWriter ? true : juris.siteEuIds.length > 0))
          .sort((a, b) => {
            const aSeg = a.pathSegments[1] || '';
            const bSeg = b.pathSegments[1] || '';
            return aSeg.localeCompare(bSeg);
          }),
      },
    [isUserLREWriter, jurisdictionData]
  );

  const firstMySiteIndex =
    !jurisdictionDataCleaned || jurisdictionDataCleaned.jurisdictions.length === 0
      ? 0
      : (() => {
          const index = jurisdictionDataCleaned.jurisdictions.findIndex(
            (juris) => juris.path.includes('.') && juris.siteEuIds.length > 0
          );
          if (
            jurisdictionDataCleaned.jurisdictions[index]?.path === 'United_States_of_America' &&
            jurisdictionDataCleaned.jurisdictions.length > 1
          ) {
            return index + 1;
          }
          return index !== -1 ? index : 0;
        })();

  const alphabetize = jurisdictionData !== undefined;

  const form = useFormContext();
  const { control, watch, resetField, setValue } = form;
  const watching = watch(controllerName);

  const [selectedLabel, setSelectedLabel] = useState<string>('');

  useEffect(() => {
    if (jurisdictionData && selectedLabel === '' && jurisdictionDataCleaned?.jurisdictions[firstMySiteIndex]) {
      const label = jurisdictionDataCleaned.jurisdictions[firstMySiteIndex].pathSegments[1] ?? '';
      setSelectedLabel(label);
      setValue(
        controllerName,
        [
          {
            label,
            jurisdictionData: jurisdictionDataCleaned.jurisdictions[firstMySiteIndex],
          },
        ],
        { shouldDirty: false }
      );
    }
  }, [jurisdictionData, jurisdictionDataCleaned, firstMySiteIndex, selectedLabel, setValue, controllerName]);

  const [selectedIndex, setSelectedIndex] = useState<number>(defaultDateComparisonPeriod || 0);
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | HTMLButtonElement | null>(null);
  const [resetEnabled, setResetEnabled] = useState(false);
  const { updateMultiSelectedSites, multiSelectedSites } = useSites();
  const [showNewList, setShowNewList] = useState(false);
  const [prevNestedOption, setPrevNestedOption] = useState<ExtendedJurisdiction>();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchBox, setSearchBox] = useState<string[]>(options.map((group) => `${group.label} ${group.subtext}`));
  const [searchValue, setSearchValue] = useState<string>('');

  const [stateLocalities, setStateLocalities] = useState<ExtendedJurisdiction[]>([]);
  const showNestedJurisdictions = jurisdictionData
    ? stateLocalities && stateLocalities.length > 0 && showNewList
    : false;

  const [currentNestingLevel, setCurrentNestingLevel] = useState<number>(1);
  const [navigationHistory, setNavigationHistory] = useState<ExtendedJurisdiction[]>([]);

  const uniqueTabs: string[] = getUniqueTabs(options, genericTabs);
  const initialTabValue = jurisdictionData ? t('labor.mySites') : uniqueTabs[0];
  const [tabValue, setTabValue] = useState<string>(initialTabValue);
  useEffect(() => {
    setTabValue(initialTabValue);
  }, [initialTabValue]);

  const previousOptionsRef = useRef<SingleSelectGroup[]>();
  const menuListRef = useRef<HTMLUListElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  let currentSiteInfo: SiteInfo[];
  if (currentSite) {
    currentSiteInfo = getSiteInfo([currentSite]);
  }

  // update sites when currentSite changes
  useEffect(() => {
    if (currentSite) {
      const selectedGroup = options.find((group) => group.label === selectedLabel);
      if (selectedGroup) {
        const site: SiteInfo[] = getSiteInfo([selectedGroup]);
        if (JSON.stringify(site) !== JSON.stringify(multiSelectedSites)) {
          updateMultiSelectedSites(site);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateMultiSelectedSites, selectedLabel, currentSite, options]);

  // set value of filter
  useEffect(() => {
    if (!anchorEl && currentSite && multiSelectedSites.length > 0) {
      const storedSites = multiSelectedSites.map((site) => ({
        label: site.name ?? '',
        subtext: site.referenceId,
        id: site.enterpriseUnitId ?? '',
      }));
      setValue(controllerName, storedSites, { shouldValidate: true });
      setSelectedLabel(storedSites[0].label);
    } else if (!anchorEl && currentSite) {
      setValue(controllerName, [currentSite], { shouldValidate: true });
      setSelectedLabel(currentSite.label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memorize site counts for all jurisdiction-level combinations
  const siteCountsCache = useMemo(() => {
    const cache = new Map<string, number>();

    if (jurisdictionDataCleaned) {
      jurisdictionDataCleaned.jurisdictions.forEach((juris) => {
        juris.pathSegments.forEach((segment, level) => {
          if (segment) {
            const key = `${segment}-${level}`;
            // Get all jurisdictions that match this segment at this level
            const matchingJurisdictions = jurisdictionDataCleaned.jurisdictions.filter(
              (j) => j.pathSegments[level] === segment
            );

            const allSiteEuIds = new Set<string>();
            matchingJurisdictions.forEach((j) => {
              j.siteEuIds.forEach((siteId) => allSiteEuIds.add(siteId));
            });

            cache.set(key, allSiteEuIds.size);
          }
        });
      });
    }

    return cache;
  }, [jurisdictionDataCleaned]);

  // Create a lookup map for jurisdictions by path
  const jurisdictionPathMap = useMemo(() => {
    const pathMap = new Map<string, ExtendedJurisdiction>();

    if (jurisdictionDataCleaned) {
      jurisdictionDataCleaned.jurisdictions.forEach((juris) => {
        pathMap.set(juris.path, juris);
      });
    }

    return pathMap;
  }, [jurisdictionDataCleaned]);

  // Memorize which jurisdictions have children at each level
  const jurisdictionChildrenMap = useMemo(() => {
    const childrenMap = new Map<string, boolean>();

    if (jurisdictionDataCleaned) {
      jurisdictionDataCleaned.jurisdictions.forEach((parentJuris) => {
        // Check each level to see if this jurisdiction has children
        for (let level = 0; level < parentJuris.pathSegments.length; level += 1) {
          const key = `${parentJuris.path}-${level}`;

          // Only compute if not already cached
          if (!childrenMap.has(key)) {
            const hasChildren = jurisdictionDataCleaned.jurisdictions.some((childJuris) => {
              // Check if all parent segments match up to the target level
              for (let i = 0; i <= level; i += 1) {
                if (childJuris.pathSegments[i] !== parentJuris.pathSegments[i]) {
                  return false;
                }
              }
              // Check if child has segment at the next level
              return childJuris.pathSegments[level + 1] !== undefined;
            });
            childrenMap.set(key, hasChildren);
          }
        }
      });
    }

    return childrenMap;
  }, [jurisdictionDataCleaned]);

  // Memorize which top-level jurisdictions have children at the second level
  const secondLevelChildrenMap = useMemo(() => {
    const childrenMap = new Map<string, boolean>();

    if (jurisdictionDataCleaned) {
      // Get all unique first-level segments
      const firstLevelSegments = new Set<string>();
      jurisdictionDataCleaned.jurisdictions.forEach((juris) => {
        if (juris.pathSegments[1]) {
          firstLevelSegments.add(juris.pathSegments[1]);
        }
      });

      // Check each first-level segment for second-level children
      firstLevelSegments.forEach((segment) => {
        const hasChildren = jurisdictionDataCleaned.jurisdictions.some(
          (juris) => juris.pathSegments[1] === segment && juris.pathSegments[2] !== undefined
        );
        childrenMap.set(segment, hasChildren);
      });
    }

    return childrenMap;
  }, [jurisdictionDataCleaned]);

  // Gets the total sites returned for a given jurisdiction at any level
  const getTotalSites = useCallback(
    (jurisdiction: string, level: number) => {
      const key = `${jurisdiction}-${level}`;
      return siteCountsCache.get(key) || 0;
    },
    [siteCountsCache]
  );

  // Gets the total sites for a specific jurisdiction path (used for nested displays)
  const getTotalSitesForPath = useCallback((jurisdiction: ExtendedJurisdiction) => jurisdiction.siteEuIds.length, []);

  // Helper function to check if parent segments match
  const doParentSegmentsMatch = useCallback(
    (jurisdiction: ExtendedJurisdiction, parentJurisdiction: ExtendedJurisdiction, targetLevel: number) => {
      for (let i = 0; i < targetLevel; i += 1) {
        if (jurisdiction.pathSegments[i] !== parentJurisdiction.pathSegments[i]) {
          return false;
        }
      }
      return true;
    },
    []
  );

  // Helper function to check if a jurisdiction has children at the next level
  const hasChildJurisdictions = useCallback(
    (parentJurisdiction: ExtendedJurisdiction, currentLevel: number) => {
      const key = `${parentJurisdiction.path}-${currentLevel}`;
      return jurisdictionChildrenMap.get(key) || false;
    },
    [jurisdictionChildrenMap]
  );

  // Helper function to check if a top-level jurisdiction has children (for the main options view)
  const hasChildrenAtSecondLevel = useCallback(
    (jurisdictionLabel: string) => secondLevelChildrenMap.get(jurisdictionLabel) || false,
    [secondLevelChildrenMap]
  );

  const getChildLocalities = useCallback(
    (parentJurisdiction: ExtendedJurisdiction, targetLevel: number) => {
      if (!jurisdictionDataCleaned?.jurisdictions) return [];

      // Map to track unique jurisdictions by their full path at the target level
      const uniqueChildren = new Map<string, ExtendedJurisdiction>();

      jurisdictionDataCleaned.jurisdictions
        .filter((jurisdiction) => {
          // Checking if this jurisdiction has the target level segment
          if (!jurisdiction.pathSegments[targetLevel]) return false;

          // Checking if all parent segments match
          return doParentSegmentsMatch(jurisdiction, parentJurisdiction, targetLevel);
        })
        .forEach((jurisdiction) => {
          // Using full path up to the target level as the unique key, ensures different paths leading to same locality names are kept separate
          const pathKey = jurisdiction.pathSegments.slice(0, targetLevel + 1).join('.');

          // Only add if we haven't seen this exact path before, or if this jurisdiction has more sites
          if (
            !uniqueChildren.has(pathKey) ||
            (uniqueChildren.get(pathKey)?.siteEuIds.length || 0) < jurisdiction.siteEuIds.length
          ) {
            uniqueChildren.set(pathKey, jurisdiction);
          }
        });

      return Array.from(uniqueChildren.values());
    },
    [jurisdictionDataCleaned, doParentSegmentsMatch]
  );

  // Auto-open nested list if showCityList is set by parent (LaborRulesMainView)
  useEffect(() => {
    if (
      form.getValues &&
      form.getValues('showCityList') &&
      watching[0]?.jurisdictionData &&
      watching[0]?.jurisdictionData.path &&
      watching[0]?.jurisdictionData.path.split('.').length > 2
    ) {
      const pathSegments = watching[0].jurisdictionData.path.split('.').map((seg: string) => seg.replaceAll('_', ' '));
      const currentLevel = pathSegments.length - 1;

      // Find the parent jurisdiction for the current level using lookup map
      const parentPath = pathSegments.slice(0, -1).join('.').replaceAll(' ', '_');
      const parentJurisdiction = jurisdictionPathMap.get(parentPath);

      if (parentJurisdiction) {
        setNavigationHistory([parentJurisdiction]);
        setCurrentNestingLevel(currentLevel);
        setStateLocalities(getChildLocalities(parentJurisdiction, currentLevel));
        setShowNewList(true);
      }

      form.setValue('showCityList', false, { shouldDirty: false });
    }
    // eslint-disable-next-line
  }, [form, watching, setShowNewList, setStateLocalities, getChildLocalities, jurisdictionPathMap]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLInputElement | HTMLButtonElement>) => {
      setAnchorEl(event.currentTarget);
      if (selectedLabel === '' && predefined) {
        setSelectedLabel(options[0].label);
      }
      setSearchBox(options.map((group) => `${group.label} ${group.subtext}`));
    },
    // eslint-disable-next-line
    [options, predefined]
  );

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFormSubtext = useCallback(
    (index: number) => {
      if (options[index].subtext?.includes(' - ')) {
        return {
          startDate: dayjs(options[index].subtext?.split(' - ')[0]).format('YYYY-MM-DD'),
          endDate: dayjs(options[index].subtext?.split(' - ')[1]).format('YYYY-MM-DD'),
        };
      }
      return {
        startDate: dayjs(options[index].subtext).format('YYYY-MM-DD'),
        endDate: dayjs(options[index].subtext).format('YYYY-MM-DD'),
      };
    },
    [options]
  );

  const handleReset = useCallback(
    (isFullRest = false) => {
      if (!useAsDateComparison) resetField(controllerName);
      if (!useAsDateComparison && isFullRest) setValue(controllerName, [], { shouldValidate: true });
      if (useAsDateComparison) {
        resetField(controllerName);
        setSelectedLabel(options[defaultDateComparisonPeriod].label);
        setSelectedIndex(defaultDateComparisonPeriod);
        setValue(
          controllerName,
          {
            label: Object.values(DateComparisonRanges).find(
              (val) => t(`globalFilters.comparisonDateRanges.${val}`) === options[defaultDateComparisonPeriod].label
            ),
            ...handleFormSubtext(defaultDateComparisonPeriod),
          },
          { shouldDirty: false }
        );
        previousOptionsRef.current = options;
      }
      if (currentSite) {
        setValue(controllerName, [currentSite], { shouldValidate: true });
        setSelectedLabel(currentSite.label);
      }
      if (!predefined && !useAsDateComparison && !currentSite && !jurisdictionDataCleaned) setSelectedLabel('');
      if (useTabs) setTabValue(form.getValues(`${controllerName}.tab`) || uniqueTabs[0] || '');
      if (jurisdictionDataCleaned && jurisdictionDataCleaned.jurisdictions.length > 1) {
        setValue(
          controllerName,
          [
            {
              label: jurisdictionDataCleaned.jurisdictions[firstMySiteIndex].pathSegments[1],
              jurisdictionData: jurisdictionDataCleaned.jurisdictions[firstMySiteIndex],
            },
          ],
          { shouldDirty: false }
        );
        setPrevNestedOption(undefined);
        setShowNewList(false);
      }
      setResetEnabled(false);
      if (!jurisdictionDataCleaned) handleClose();
    },
    [
      useAsDateComparison,
      resetField,
      controllerName,
      currentSite,
      predefined,
      jurisdictionDataCleaned,
      useTabs,
      form,
      uniqueTabs,
      options,
      defaultDateComparisonPeriod,
      setValue,
      handleFormSubtext,
      t,
      firstMySiteIndex,
    ]
  );

  const handleBackgroundColor = (label: string) => {
    const checkIfLabelSelected = watching[0]?.label === label || selectedLabel === label;
    const checkIfPredefined =
      predefined && options.findIndex((group) => group.label === label) === 0 && selectedLabel === '';
    const checkIfJurisdictions =
      jurisdictionData &&
      watching[0] &&
      !showNewList &&
      watching[0].jurisdictionData?.pathSegments &&
      watching[0].jurisdictionData?.pathSegments[1] === label &&
      watching[0]?.label === selectedLabel;
    const checkIfNestedJurisdictions = jurisdictionData && watching[0] && showNewList && watching[0]?.label === label;

    let color =
      checkIfLabelSelected || checkIfPredefined || checkIfJurisdictions || checkIfNestedJurisdictions ? '#d7edff' : '';

    if (color !== '' && theme.palette.mode === 'dark') {
      color = '#1e3756';
    }

    return color;
  };

  const getDateRange = (subtext: string | undefined, label: string) => {
    const dates = subtext?.split(' - ');
    return dates?.length === 2
      ? {
          label: Object.values(DateComparisonRanges).find(
            (val) => t(`globalFilters.comparisonDateRanges.${val}`) === label
          ),
          startDate: dayjs(dates[0]).format('YYYY-MM-DD'),
          endDate: dayjs(dates[1]).format('YYYY-MM-DD'),
        }
      : {
          label: Object.values(DateComparisonRanges).find(
            (val) => t(`globalFilters.comparisonDateRanges.${val}`) === label
          ),
          startDate: dayjs(subtext).format('YYYY-MM-DD'),
          endDate: dayjs(subtext).format('YYYY-MM-DD'),
        };
  };

  const shouldEnableReset = () => {
    if (
      form.formState.isDirty &&
      selectedLabel !== '' &&
      selectedLabel !== undefined &&
      !predefined &&
      !useAsDateComparison &&
      !jurisdictionData &&
      selectedLabel !== currentSite?.label
    ) {
      return true;
    }
    if (!useAsDateComparison && selectedLabel !== '' && selectedLabel !== undefined && isTextField) {
      return true;
    }
    if (useAsDateComparison && selectedLabel !== options[defaultDateComparisonPeriod].label) {
      return true;
    }
    if (predefined && selectedLabel !== options[0].label) {
      return true;
    }
    if (jurisdictionData) {
      const isMatchingJurisdiction =
        (isUserLREWriter &&
          watching[0]?.label === jurisdictionDataCleaned?.jurisdictions[firstMySiteIndex].pathSegments[1]) ||
        (!isUserLREWriter && watching[0]?.label === jurisdictionDataCleaned?.jurisdictions[0].pathSegments[1]);
      return !isMatchingJurisdiction;
    }
    return false;
  };

  useEffect(() => {
    setResetEnabled(shouldEnableReset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultDateComparisonPeriod, form, options, predefined, selectedLabel, useAsDateComparison]);

  useEffect(() => {
    const allSearchResults = showNewList
      ? stateLocalities.map((juris) => juris.pathSegments[2])
      : options
          .map((group) => `${group.label} ${group.subtext}`)
          .filter((label) => label.toLowerCase().indexOf(searchValue.toLowerCase()) > -1);
    setSearchBox(allSearchResults);
  }, [options, searchValue, showNewList, stateLocalities]);

  useEffect(() => {
    const handleNonDateComparison = () => {
      if (watching[0]?.id) {
        const selectedOption = options.find((option) => option.id === watching[0].id);
        setSelectedLabel(selectedOption?.overrideLabel ?? selectedOption?.label ?? '');
        return;
      }

      setSelectedLabel(watching[0]?.overrideLabel ?? watching[0]?.label ?? '');
    };

    const handleDateComparison = () => {
      if (JSON.stringify(options) !== JSON.stringify(previousOptionsRef.current) && resetEnabled) {
        setSelectedLabel(options[selectedIndex].label);
        setValue(
          controllerName,
          {
            label: Object.values(DateComparisonRanges).find(
              (val) => t(`globalFilters.comparisonDateRanges.${val}`) === options[selectedIndex].label
            ),
            ...handleFormSubtext(selectedIndex),
          },
          { shouldDirty: false }
        );
        previousOptionsRef.current = options;
      } else {
        setSelectedLabel(
          options[selectedIndex]?.subtext?.includes(' - ')
            ? options.find(
                (group) =>
                  group.subtext === `${dayjs(watching.startDate).format('L')} - ${dayjs(watching.endDate).format('L')}`
              )?.label || ''
            : options.find((group) => group.subtext === dayjs(watching.startDate).format('L'))?.label || ''
        );
      }
    };

    if (watching[0] && !useAsDateComparison) {
      handleNonDateComparison();
    }

    if (useAsDateComparison) {
      handleDateComparison();
    }
  }, [
    controllerName,
    handleFormSubtext,
    options,
    resetEnabled,
    selectedIndex,
    selectedLabel,
    setValue,
    showNewList,
    t,
    useAsDateComparison,
    watching,
  ]);

  useEffect(() => {
    if (!debouncedSearchFn) setSearchValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  // Reset scroll position when navigating to deeper nesting levels
  useEffect(() => {
    if (showNewList && containerRef.current) {
      // Find the scrollable parent container (FilterFrame's scroll container)
      let scrollableParent = containerRef.current.parentElement;
      const maxIterations = 20;
      let iterations = 0;

      while (scrollableParent && iterations < maxIterations) {
        // Break if we've reached document.body to avoid infinite loop
        if (scrollableParent === document.body) {
          break;
        }

        const computedStyle = window.getComputedStyle(scrollableParent);
        if (
          computedStyle.overflow === 'auto' ||
          computedStyle.overflowY === 'auto' ||
          scrollableParent.scrollHeight > scrollableParent.clientHeight
        ) {
          scrollableParent.scrollTop = 0;
          break;
        }
        scrollableParent = scrollableParent.parentElement;
        iterations += 1;
      }
    }
  }, [currentNestingLevel, showNewList]);

  const ErrorMessage = (
    <Alert
      sx={styles.getAlertStyles()}
      data-testid='alert-box'
      action={
        <Button
          sx={styles.getRefreshButtonStyles()}
          variant='text'
          data-testid='refresh-button'
          onClick={errorHandling?.refetch}
        >
          <Typography fontWeight={500} fontSize='inherit' color='inherit'>
            {t('labor.refresh')}
          </Typography>
        </Button>
      }
      severity='error'
    >
      {t('globalFilters.failLoadingSites')}
    </Alert>
  );

  const backButton = showNewList ? (
    <IconButton
      data-testid='single-select-back-button'
      sx={styles.getBackButtonStyles(theme)}
      onClick={() => {
        if (navigationHistory.length > 1) {
          // Going back one level
          const newHistory = [...navigationHistory];
          newHistory.pop();
          setNavigationHistory(newHistory);
          setCurrentNestingLevel(currentNestingLevel - 1);

          const parentJurisdiction = newHistory[newHistory.length - 1];
          setStateLocalities(getChildLocalities(parentJurisdiction, currentNestingLevel - 1));
        } else {
          // Going back to root level
          setShowNewList(false);
          setCurrentNestingLevel(1);
          setNavigationHistory([]);
        }
        setSearchValue('');
      }}
    >
      <WestIcon />
    </IconButton>
  ) : null;

  const generateNestedOptions = (field: ControllerRenderProps<FieldValues, string>) =>
    stateLocalities
      .filter((jurisdiction) =>
        jurisdiction.pathSegments[currentNestingLevel]?.toLowerCase().includes(searchValue.toLowerCase())
      )
      .sort((juris1, juris2) =>
        juris1.pathSegments[currentNestingLevel].localeCompare(juris2.pathSegments[currentNestingLevel])
      )
      .map((group, idx) => (
        <ExpandedMenuItem
          key={`${group.pathSegments[currentNestingLevel]}-key`}
          backgroundColor={handleBackgroundColor(group.pathSegments[currentNestingLevel] || '')}
          onClick={() => {
            const hasChildren = hasChildJurisdictions(group, currentNestingLevel);

            // Selecting the jurisdiction first
            setSelectedIndex(idx + 1);
            setPrevNestedOption(group);
            setSelectedLabel(group.pathSegments[currentNestingLevel]);
            field.onChange([
              {
                label: group.pathSegments[currentNestingLevel],
                jurisdictionData: group,
              },
            ] as SingleSelectGroup[]);

            if (hasChildren) {
              // Navigate deeper but keep the selection
              const newHistory = [...navigationHistory, group];
              setNavigationHistory(newHistory);
              setCurrentNestingLevel(currentNestingLevel + 1);
              setStateLocalities(getChildLocalities(group, currentNestingLevel + 1));
              setSearchValue('');
            } else {
              // No children, close dropdown
              handleClose();
            }
          }}
          text={group.pathSegments[currentNestingLevel]}
        >
          <Grid container direction='row' sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography sx={styles.getOptionTypographyStyles()}>
              {`${group.pathSegments[currentNestingLevel]} ${
                getTotalSitesForPath(group) ? `(${getTotalSitesForPath(group)})` : ''
              }`}
            </Typography>
            {hasChildJurisdictions(group, currentNestingLevel) && (
              <ArrowForwardIosIcon sx={{ width: '20px', height: '20px', color: '#6F6F73' }} />
            )}
          </Grid>
        </ExpandedMenuItem>
      ));

  // Creates an 'All' option that is displayed first in the nested options
  const generateNestedOptionsAll = (field: ControllerRenderProps<FieldValues, string>) => {
    const elements: JSX.Element[] = [];
    const nestedOptions = generateNestedOptions(field);

    // Find the "All" option for the current level
    const currentParent = navigationHistory[navigationHistory.length - 1];
    let allOption: ExtendedJurisdiction | undefined;

    if (currentParent) {
      // Look for a jurisdiction that matches the parent path but doesn't have the next level
      allOption = jurisdictionDataCleaned?.jurisdictions.find((juris) => {
        // Check if all parent segments match
        for (let i = 0; i < currentNestingLevel; i += 1) {
          if (juris.pathSegments[i] !== currentParent.pathSegments[i]) return false;
        }
        // Should not have the current level segment (this makes it an "All" option)
        return !juris.pathSegments[currentNestingLevel];
      });
    }

    if (allOption) {
      const allJurisdictions = (
        <ExpandedMenuItem
          key={`${allOption.pathSegments[currentNestingLevel - 1]}-all-key`}
          backgroundColor={handleBackgroundColor(allOption.pathSegments[currentNestingLevel - 1])}
          onClick={() => {
            setSelectedIndex(0);
            field.onChange([
              {
                label: allOption?.pathSegments[currentNestingLevel - 1] ?? '',
                jurisdictionData: allOption,
              },
            ] as SingleSelectGroup[]);
            handleClose();
            setShowNewList(false);
            setCurrentNestingLevel(1);
            setNavigationHistory([]);
          }}
          text={`${allOption.pathSegments[currentNestingLevel - 1]} ${t('globalFilters.all')}`}
        >
          <Typography sx={styles.getOptionTypographyStyles()}>
            {`${allOption.pathSegments[currentNestingLevel - 1]} ${t('globalFilters.all')}`}
          </Typography>
        </ExpandedMenuItem>
      );

      elements.push(allJurisdictions);
    }
    elements.push(...nestedOptions);

    return elements;
  };

  const generateOptions = (field: ControllerRenderProps<FieldValues, string>) => {
    let filteredOptions = options
      .filter((group) => (useTabs ? tabValue === t('globalFilters.allTab') || group.tab === tabValue : group))
      .filter((group) => searchBox.includes(`${group.label} ${group.subtext}`));

    if (useTabs && tabValue === t('globalFilters.allTab')) {
      const noneOption = filteredOptions.find((group) => group.label === t('inventory.menu.mapping.none'));
      const nonNoneOptions = filteredOptions.filter((group) => group.label !== t('inventory.menu.mapping.none'));

      // If there's a "None" option, only show one instance
      if (noneOption) {
        // Use the first None option but set its tab to 'All'
        const allTabNone = {
          ...noneOption,
          tab: t('globalFilters.allTab'),
        };
        filteredOptions = [allTabNone, ...nonNoneOptions];
      }
    }

    const noneOptions = filteredOptions.filter((group) => group.label === t('inventory.menu.mapping.none'));
    const otherOptions = filteredOptions.filter((group) => group.label !== t('inventory.menu.mapping.none'));
    filteredOptions = [...noneOptions, ...otherOptions];
    return filteredOptions.map((group, idx) => {
      const grid = (
        <Grid container direction='row' sx={styles.getMainGridContainerStyles()}>
          <Box sx={styles.getTextContainerStyles(!!(jurisdictionDataCleaned && hasChildrenAtSecondLevel(group.label)))}>
            <Typography sx={styles.getWrappableTextStyles()} noWrap variant='inherit'>{`${group.label}${
              jurisdictionDataCleaned && group.jurisdictionData && getTotalSites(group.label, 1) > 0
                ? ` (${getTotalSites(group.label, 1)})`
                : ''
            }`}</Typography>
            <Typography sx={styles.getSubtextTypographyStyles(theme)}>{group.subtext}</Typography>
          </Box>
          <Box sx={styles.getRightElementsContainerStyles()}>
            {group.isNameRecentlyUpdated && (
              <Chip label={t('globalFilters.newNameChipTitle')} color='secondary' sx={styles.getChipStyles()} />
            )}
            {jurisdictionDataCleaned && hasChildrenAtSecondLevel(group.label) && (
              <ArrowForwardIosIcon sx={styles.getArrowForwardIconStyles()} />
            )}
          </Box>
        </Grid>
      );
      return (
        <ExpandedMenuItem
          key={`${group.label} ${group.subtext}-key`}
          backgroundColor={handleBackgroundColor(group.label)}
          text={group.label}
          onClick={() => {
            setSelectedIndex(idx);
            if (jurisdictionData) {
              if (group.label !== prevNestedOption?.pathSegments[1]) {
                setPrevNestedOption(undefined);
                field.onChange(useAsDateComparison ? getDateRange(group.subtext, group.label) : [group]);
              }
            } else {
              field.onChange(useAsDateComparison ? getDateRange(group.subtext, group.label) : [group]);
            }
            if (jurisdictionData) {
              const parentJurisdiction = jurisdictionDataCleaned?.jurisdictions.find(
                (juris) => juris.pathSegments[1] === group.label && !juris.pathSegments[2]
              );
              if (parentJurisdiction) {
                const localities = getChildLocalities(parentJurisdiction, 2);
                setStateLocalities(localities);
                if (localities?.length > 0) {
                  setNavigationHistory([parentJurisdiction]);
                  setCurrentNestingLevel(2);
                  setShowNewList(true);
                } else {
                  setShowNewList(false);
                  handleClose();
                }
                setSearchValue('');
              }
            }
            if (!jurisdictionData) {
              handleClose();
            }
          }}
        >
          {grid}
        </ExpandedMenuItem>
      );
    });
  };

  const SingleSelectComponent = (
    <Controller
      name={controllerName}
      control={control}
      render={({ field }) => (
        <Box>
          {errorHandling?.showError && ErrorMessage}
          <Box
            ref={containerRef}
            aria-label='single-select-popup'
            data-testid='single-select-popup'
            sx={styles.getSingleSelectPopupStyles(!!jurisdictionData)}
          >
            {useTabs && !showNewList && (
              <TabBar
                activeTab={tabValue}
                tabs={uniqueTabs}
                setActiveTab={setTabValue}
                alphabetize={alphabetize}
                isDrawerSelect={isDrawerSelect}
              />
            )}
            {options.filter((group) =>
              useTabs ? tabValue === t('globalFilters.allTab') || group.tab === tabValue : group
            ).length > 9 || !isNil(debouncedSearchFn) ? (
              <SearchBar
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                showClearIcon={searchValue.length > 0}
                debouncedSearchFn={debouncedSearchFn}
              />
            ) : null}
            <MenuList ref={menuListRef}>
              {showNestedJurisdictions ? generateNestedOptionsAll(field) : generateOptions(field)}
            </MenuList>
          </Box>
        </Box>
      )}
    />
  );

  const siteButton = useMemo(
    () => (
      <SiteFilterButton
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        selected={multiSelectedSites}
        optionsLength={options.length}
        currentSite={currentSiteInfo}
        resetEnabled={resetEnabled}
        buttonSize={buttonSize}
        handleReset={handleReset}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedLabel, resetEnabled, anchorEl, buttonSize, handleReset, options.length, currentSite, multiSelectedSites]
  );

  const button = useMemo(
    () => (
      <SingleSelectButton
        anchorEl={anchorEl}
        buttonSize={buttonSize}
        buttonText={buttonText || ''}
        handleClick={handleClick}
        isMobile={isMobile}
        options={options}
        selectedLabel={selectedLabel}
        controllerName={controllerName}
        handleReset={handleReset}
        predefined={predefined || false}
        useAsDateComparison={useAsDateComparison || false}
        resetValue={resetEnabled}
        useAsJurisdiction={!!jurisdictionData}
        labelText={labelText}
        isDrawerSelect={isDrawerSelect}
        preDefinedFilterSelection={preDefinedFilterSelection || false}
        isDropDownSelect={isDropDownSelect}
        hasError={hasError}
        disabled={disabled}
        required={required}
        customStartIcon={customStartIcon}
      />
    ),
    [
      anchorEl,
      buttonSize,
      buttonText,
      handleClick,
      isMobile,
      options,
      selectedLabel,
      controllerName,
      handleReset,
      predefined,
      useAsDateComparison,
      resetEnabled,
      jurisdictionData,
      labelText,
      isDrawerSelect,
      preDefinedFilterSelection,
      isDropDownSelect,
      hasError,
      disabled,
      required,
      customStartIcon,
    ]
  );

  const standardTextField = useMemo(
    () => (
      <SingleSelectTextField
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        controllerName={controllerName}
        resetEnabled={resetEnabled}
        handleReset={handleReset}
        selectedLabel={selectedLabel}
        useAsDateComparison={useAsDateComparison || false}
        textFieldLabel={textFieldLabel}
        isRequired={isRequired}
      />
    ),
    [
      anchorEl,
      controllerName,
      handleReset,
      isRequired,
      resetEnabled,
      selectedLabel,
      textFieldLabel,
      useAsDateComparison,
    ]
  );

  // Update the title to show the full path
  const getNestedTitle = () => {
    if (!jurisdictionData || !showNestedJurisdictions || navigationHistory.length === 0) {
      return titleText;
    }

    const currentParent = navigationHistory[navigationHistory.length - 1];
    return currentParent.pathSegments.slice(1, currentNestingLevel).join(' > ');
  };

  return onlyFilterContainer ? (
    SingleSelectComponent
  ) : (
    <FilterContainer
      resetEnabled={resetEnabled}
      handleClose={handleClose}
      anchorEl={anchorEl}
      button={currentSite ? siteButton : button}
      title={getNestedTitle()}
      body={SingleSelectComponent}
      resetFunction={handleReset}
      backButton={backButton}
      showNewList={showNewList}
      useHeader={useHeader}
      useReset={useReset}
      isDrawerSelect={isDrawerSelect}
      isDropDownSelect={isDropDownSelect}
      textField={isTextField && standardTextField}
    />
  );
}

SingleSelect.defaultProps = {
  useTabs: false,
  preDefinedFilterSelection: false,
  predefined: false,
  useAsDateComparison: false,
  currentSite: undefined,
  defaultDateComparisonPeriod: 0,
  jurisdictionData: undefined,
  labelText: undefined,
  useHeader: undefined,
  useReset: undefined,
  genericTabs: undefined,
  isDrawerSelect: false,
  errorHandling: undefined,
  isDropDownSelect: false,
  hasError: false,
  disabled: false,
  required: false,
  debouncedSearchFn: undefined,
  customStartIcon: undefined,
  isTextField: false,
  textFieldLabel: '',
  isRequired: false,
};

export default SingleSelect;
